import React, { useState, useEffect } from 'react'
import { Route, Switch, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCurrentUserAndHandler } from 'store/currentUserManager/currentUserManager'
import { domains, LD_CREDS } from 'lib/config'
import lmsApi from 'lib/CfcLmsApi'
import documentServiceApi from 'lib/DocumentServiceApi'
import { FooterContainer as Footer } from '@cfc/secondstep-header'
import { useAuth0 } from '@auth0/auth0-react'
import isEmpty from 'lodash/isEmpty'
import { FlagsSingleton } from 'flags'
import { NotificationList } from 'components/Notifications'
import Loading from 'components/Loading/Loading'
import ModalContainer from 'containers/modal/ModalContainer'
import AdminNavBar from 'components/AdminNavBar'
import EditProfileForm from 'containers/account/EditProfileForm'
import CssAdmin from 'containers/css-admin/CssAdmin'
import GlobalNav from 'components/GlobalNav'
import FirstTimeUserNav from 'components/FirstTimeUserNav'
import { ThemeProvider } from 'styled-components'
import theme from './theme/main'
import { setCookie } from 'lib/util'
import ssApi from 'lib/Api'
import { RootState } from 'types'
import { MainContainer } from './App.styles'

interface CurrentUser {
  isFirstTimeUser?: boolean
  hasUpdatedProfile?: boolean
  isAdmin?: boolean
  isCssAdmin?: boolean
  isOperationsAdmin?: boolean
  isTechnologyAdmin?: boolean
  displayName?: string
  email?: string
  roles?: string[]
}

const App: React.FC = () => {
  const { currentUser, isLoading } = useSelector(
    (state: RootState) => state.currentUserManager,
  )

  // ============= Launch DARKLY USER CONTEXT ================
  const [isLoadingUserContext, setLoadingUserContext] = useState<
    boolean | null
  >(null)
  const [hasUserContextBeenCalled, setContextHasBeenCalled] = useState(false)
  const hasAllInitialStateBeenLoaded =
    !isEmpty(currentUser) && !isLoadingUserContext && hasUserContextBeenCalled

  useEffect(() => {
    const defaultLaunchDarklyUser = LD_CREDS.user.key

    const shouldUpdateLaunchDarklyUserContext =
      !isEmpty(currentUser) &&
      !hasUserContextBeenCalled &&
      FlagsSingleton.getUserContext().key === defaultLaunchDarklyUser

    if (shouldUpdateLaunchDarklyUserContext) {
      handleUpdateUserContext({
        currentUser: currentUser as CurrentUser,
      })
    }
  }, [currentUser, hasUserContextBeenCalled])

  const handleUpdateUserContext = async ({
    currentUser,
  }: {
    currentUser: CurrentUser
  }) => {
    setContextHasBeenCalled(true)
    const { displayName, email, roles } = currentUser || {}
    const updatedUserConfigForLaunchDarkly = {
      userEmail: email,
      roles,
      name: displayName,
    }
    setLoadingUserContext(true)
    try {
      await FlagsSingleton.updateUserContext(updatedUserConfigForLaunchDarkly)
    } catch {
      // Failing silently and we just serve a false variation
    }
    setLoadingUserContext(false)
  }
  // ============= END Launch DARKLY USER CONTEXT ================

  const [showFooter, setShowFooter] = useState(false)
  const history = useHistory()
  const dispatch = useDispatch()

  const auth0 = useAuth0()
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user,
    loginWithRedirect,
    logout,
  } = auth0

  useEffect(() => {
    const handleAuthUpdate = async () => {
      if (!auth0Loading && !isAuthenticated) {
        let pathname = window.location.pathname + window.location.search
        if (pathname === '/') pathname = '/css-admin/licenses'
        setCookie('pathname', pathname)
        loginWithRedirect()
      } else if (isAuthenticated) {
        ssApi.setAuthInstance(auth0)
        lmsApi.setAuthInstance(auth0)
        documentServiceApi.setAuthInstance(auth0)
        dispatch(fetchCurrentUserAndHandler())
      }
    }

    handleAuthUpdate()
  }, [
    auth0Loading,
    isAuthenticated,
    user?.nickname,
    auth0,
    loginWithRedirect,
    dispatch,
  ])

  const signout = () => {
    logout({ returnTo: domains.PUBLIC_SITE_DOMAIN })
  }

  const toggleFooter = (show: boolean) => setShowFooter(show)

  if (!isAuthenticated || !currentUser) return null

  return (
    <>
      <Loading
        isLoading={!currentUser && isLoading && !hasAllInitialStateBeenLoaded}
      />
      <ThemeProvider theme={theme}>
        {(currentUser as CurrentUser).isFirstTimeUser ||
        !(currentUser as CurrentUser).hasUpdatedProfile ? (
          <FirstTimeUserNav />
        ) : (
          <GlobalNav />
        )}

        <MainContainer>
          <NotificationList />
          {(currentUser as CurrentUser).isAdmin && (
            <AdminNavBar
              isCssAdmin={(currentUser as CurrentUser).isCssAdmin}
              isOperationsAdmin={(currentUser as CurrentUser).isOperationsAdmin}
              isTechnologyAdmin={(currentUser as CurrentUser).isTechnologyAdmin}
            />
          )}
          <Switch>
            <Route component={EditProfileForm} path="/account-details" />

            <Route
              path="/logout"
              render={() => {
                signout()
                history.push('/')
                return null
              }}
            />
            {currentUser && (currentUser as CurrentUser).isAdmin && (
              <Route
                path="/css-admin"
                render={props => (
                  <CssAdmin
                    {...props}
                    isCssAdmin={(currentUser as CurrentUser).isCssAdmin}
                    isOperationsAdmin={
                      (currentUser as CurrentUser).isOperationsAdmin
                    }
                    isTechnologyAdmin={
                      (currentUser as CurrentUser).isTechnologyAdmin
                    }
                    showFooter={toggleFooter}
                  />
                )}
              />
            )}
            <Route
              path="/signout"
              render={async () => {
                await logout({ returnTo: domains.PUBLIC_SITE_DOMAIN })
                return null
              }}
            />
          </Switch>
        </MainContainer>
        <ModalContainer />
        {!isLoading && showFooter && (
          <div
            style={{
              marginTop: '4.375rem',
            }}
          >
            <Footer domains={domains} />
          </div>
        )}
      </ThemeProvider>
    </>
  )
}

export default App
